<template>
    <RegisterLayout>
        <Head>
            <title>
                {{ $t('register.page_title') }}
            </title>
        </Head>

        <section class="bg-white flex flex-col h-full">
            <div class="grid grid-cols-1 xl:grid-cols-2 flex-grow">
                <div class="flex items-center justify-center px-4 sm:px-12 py-4 sm:py-20 bg-gradient-to-r from-pink-200 via-purple-400 to-indigo-500">
                    <div class="bg-white px-16 py-12 rounded-lg shadow-sm">
                        <div class="flex flex-1 flex-col justify-center items-center py-4 sm:mx-auto sm:w-full sm:max-w-md">
                            <ApplicationLogo class="mx-auto mb-4" />
                            <div>
                                <h2 class="mt-4 text-center text-2xl font-medium leading-9 tracking-tight text-slate-900">
                                    {{ $t('register.heading.part_1') }}
                                    <span class="text-indigo-500 font-bold"> {{ $t('register.heading.free') }} </span>
                                    {{ $t('register.heading.part_2') }}
                                </h2>

                                <div class="text-center mt-4 sm:mt-1">
                                    <p class="text-center font-normal text-sm text-slate-500 inline-flex items-center justify-center gap-1">
                                        <span class="inline-flex items-center rounded-full bg-indigo-100 px-1.5 text-[10px] font-bold text-indigo-700">
                                            <svg class="size-3 mr-1" width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.49954 9H21.4995M9.99954 3L7.99954 9L11.9995 20.5L15.9995 9L13.9995 3M12.6141 20.2625L21.5727 9.51215C21.7246 9.32995 21.8005 9.23885 21.8295 9.13717C21.8551 9.04751 21.8551 8.95249 21.8295 8.86283C21.8005 8.76114 21.7246 8.67005 21.5727 8.48785L17.2394 3.28785C17.1512 3.18204 17.1072 3.12914 17.0531 3.09111C17.0052 3.05741 16.9518 3.03238 16.8953 3.01717C16.8314 3 16.7626 3 16.6248 3H7.37424C7.2365 3 7.16764 3 7.10382 3.01717C7.04728 3.03238 6.99385 3.05741 6.94596 3.09111C6.89192 3.12914 6.84783 3.18204 6.75966 3.28785L2.42633 8.48785C2.2745 8.67004 2.19858 8.76114 2.16957 8.86283C2.144 8.95249 2.144 9.04751 2.16957 9.13716C2.19858 9.23885 2.2745 9.32995 2.42633 9.51215L11.385 20.2625C11.596 20.5158 11.7015 20.6424 11.8279 20.6886C11.9387 20.7291 12.0603 20.7291 12.1712 20.6886C12.2975 20.6424 12.4031 20.5158 12.6141 20.2625Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg> 
                                            BONUS
                                        </span>
                                        <span>{{ $t('register.subheading') }}</span>
                                    </p>
                                </div>

                                <div v-if="referralCode">
                                    <p class="mt-4 text-center font-medium text-sm text-slate-600">
                                        {{ $t('register.referral.part_1') }}
                                        <span class="font-bold">
                                            {{ $t('register.referral.part_2') }}
                                        </span> 🎉
                                    </p>
                                </div>

                                <!-- Display flash message if present -->

                                <div v-if="infoMessage" class="max-w-3/4">
                                    <div class="rounded-md bg-indigo-50 px-4 py-3 mt-4">
                                        <div class="flex">
                                            <div class="shrink-0">
                                                <InformationCircleIcon class="size-5 text-indigo-500" aria-hidden="true" />
                                            </div>
                                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                                <p class="text-sm text-indigo-600 font-medium">
                                                    {{ infoMessage }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-10 w-full" v-if="!selectedEmailRegistration">
                                <GoogleSignUpButton
                                    :action="handleSignUp"
                                    :disabled="!checkedNecessaryInputs"
                                />

                                <div class="mt-6" >
                                    <div class="relative">
                                        <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                            <div class="w-full border-t border-slate-100 border" />
                                        </div>
                                        <div class="relative flex justify-center text-sm font-medium leading-6">
                                            <span class="bg-white px-6 text-slate-900">
                                                {{ $t('register.form.or') }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Sign up with email -->
                                <div class="mt-6">
                                    <button @click="selectedEmailRegistration = true" class="flex w-full justify-center rounded-md uppercase bg-indigo-50 px-5 py-2 text-sm font-semibold leading-6 text-indigo-600 shadow-sm hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600 transition-all">
                                        {{ $t('register.form.email_cta_button') }}
                                    </button>
                                </div>

                            </div>

                            <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-md">
                                <form novalidate @submit.prevent="submit">
                                    <div class="mb-4" v-if="selectedEmailRegistration">
                                        <label for="name" class="block text-sm font-medium leading-6 text-slate-900">
                                            {{ $t('register.form.name_label') }}
                                        </label>
                                        <input id="name" type="text" v-model="form.name" required autofocus autocomplete="name" class="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" :placeholder="$t('register.form.name_placeholder')" />
                                        <InputError :message="form.errors.name" />
                                    </div>

                                    <div class="mb-4" v-if="selectedEmailRegistration">
                                        <label for="email" class="block text-sm font-medium leading-6 text-slate-900">
                                            {{ $t('register.form.email_label') }}
                                        </label>
                                        <input id="email" type="email" v-model="form.email" required autocomplete="email"  class="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" :placeholder="$t('register.form.email_placeholder')"/>
                                        <InputError :message="form.errors.email" />
                                    </div>

                                    <div class="mb-4" v-if="selectedEmailRegistration">
                                        <label for="password" class="block text-sm font-medium leading-6 text-slate-900">
                                            {{ $t('register.form.password_label') }}
                                        </label>
                                        <input id="password" type="password" v-model="form.password" required autocomplete="new-password" class="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" :placeholder="$t('register.form.password_placeholder')"/>
                                        <InputError :message="form.errors.password" />
                                    </div>

                                    <div class="mb-4" v-if="selectedEmailRegistration">
                                        <label for="password_confirmation" class="block text-sm font-medium leading-6 text-slate-900">
                                            {{ $t('register.form.password_confirmation_label') }}
                                        </label>
                                        <input id="password_confirmation" type="password" v-model="form.password_confirmation" required autocomplete="new-password" class="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" :placeholder="$t('register.form.password_confirmation_placeholder')"  />
                                        <InputError :message="form.errors.password_confirmation" />
                                    </div>

                                    <div class="mb-6" v-if="selectedEmailRegistration">
                                        <Listbox as="div" v-model="selectedCountry">
                                            <ListboxLabel class="block text-sm font-medium leading-6 text-slate-900">
                                                {{ $t('register.form.country_label') }}
                                            </ListboxLabel>
                                            <div class="relative">
                                                <ListboxButton class="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm sm:leading-6">
                                                    <span class="flex items-center">
                                                        <img :src="selectedCountry.avatar" alt="" class="h-3.5 flex-shrink-0 rounded-sm" />
                                                        <span class="ml-2 block truncate">{{ selectedCountry.name }}</span>
                                                    </span>
                                                    <span class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                                                        <ChevronUpDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                                                    </span>
                                                </ListboxButton>

                                                <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                                    <ListboxOptions class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                        <ListboxOption
                                                            as="template"
                                                            v-for="country in countries"
                                                            :key="country.id"
                                                            :value="country"
                                                            v-slot="{ active, selected }"
                                                            @click="userHasSelectedCountry.value = true"
                                                        >
                                                            <li :class="[active ? 'bg-indigo-600 text-white' : 'text-slate-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                                                                <div class="flex items-center">
                                                                    <img :src="country.avatar" alt="" class="h-3.5 flex-shrink-0 rounded-sm" />
                                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate']">{{ country.name }}</span>
                                                                </div>

                                                                <span v-if="selected" :class="[active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                                                                </span>
                                                            </li>
                                                        </ListboxOption>
                                                    </ListboxOptions>
                                                </transition>
                                            </div>
                                        </Listbox>
                                    </div>

                                    <div class="mb-4" v-if="selectedEmailRegistration">
                                        <div class="turnstile-container w-full" style="opacity: 0; position: absolute; left: -9999px;">
                                            <vue-turnstile
                                                :language="locale"
                                                :site-key="siteKey"
                                                v-model="form.cftoken"
                                                size="flexible"
                                                theme="light"
                                                required
                                            />
                                        </div>

                                        <InputError :message="form.errors.cftoken" />
                                    </div>

                                    <div class="mb-4">
                                        <label class="flex items-start">
                                            <input
                                                type="checkbox"
                                                v-model="form.terms"
                                                required
                                                class="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 mr-2 "
                                                :class="{ 'pulse-checkbox': !form.terms }"
                                            />
                                            <p class="text-sm">
                                                {{ $t('register.form.terms.part_1') }}
                                                <a href="/terms-of-use" class="font-bold text-indigo-500">{{ $t('register.form.terms.link') }}</a>
                                                {{ $t('register.form.terms.part_2') }}
                                                <a href="/privacy-policy" class="font-bold text-indigo-500">{{ $t('register.form.terms.link_2') }}</a>.
                                            </p>
                                        </label>
                                        <InputError class="ml-6" :message="form.errors.terms" />
                                    </div>

                                    <div class="flex items-center justify-between mt-8 flex-col" v-if="selectedEmailRegistration">
                                        <button
                                            type="submit"
                                            class="flex w-full justify-center rounded-md uppercase bg-indigo-600 px-5 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                                            :disabled="!checkedNecessaryInputsForForm"
                                        >
                                            <span v-if="isLoading">
                                                <AppSpinner :size="22" class="text-white" />
                                            </span>
                                            <span v-else>
                                                <span>
                                                    {{ $t('register.form.cta_button') }}
                                                </span>
                                            </span>

                                        </button>
                                    </div>
                                </form>

                            </div>

                            <div class="mt-8 mb-8 flex justify-center items-center w-full">
                                <SelectLanguage class="w-48 flex flex-col justify-center items-center"/>
                            </div>

                            <p class="mb-4 leading-normal text-sm text-center">
                                {{ $t('register.already_registered.part_1') }}
                                <a href="/login" class="font-bold text-indigo-500">
                                    {{ $t('register.already_registered.link') }}
                                </a>
                            </p>

                        </div>
                    </div>

                </div>
                <div class="">
                    <RegistrationFeatures />
                </div>

            </div>
        </section>
    </RegisterLayout>
</template>

<script setup>
import RegisterLayout from '@/Layouts/RegisterLayout.vue';
import InputError from '@/Components/InputError.vue';
import { Head, Link, useForm, usePage } from '@inertiajs/vue3';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import AppSpinner from "@/Components/AppSpinner.vue";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import VueTurnstile from 'vue-turnstile';
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue';
import GoogleSignUpButton from "@/Components/Auth/GoogleSignUpButton.vue";
import SelectLanguage from "@/Components/SelectLanguage.vue";
import RegistrationFeatures from "@/Components/Auth/RegistrationFeatures.vue";
import { InformationCircleIcon } from '@heroicons/vue/20/solid'

const { locale } = useI18n();

const page = usePage();
const appProps = page.props.app ? page.props.app : {}; // Add fallback

const appEnv = ref(appProps.env || 'production');

// https://developers.cloudflare.com/turnstile/troubleshooting/testing/
const siteKey = appEnv.value === 'local' ? "1x00000000000000000000AA" :'0x4AAAAAAAi3vVSJJ_RK6en_';

const isLoading = ref(false);
const selectedEmailRegistration = ref(false);
const checkedNecessaryInputs = computed(() => {
    return form.terms
});

const infoMessage = computed(() => page.props.info);

const checkedNecessaryInputsForForm = computed(() => {
    return form.name &&
        form.email &&
        form.password &&
        form.password_confirmation &&
        form.terms &&
        form.cftoken &&
        form.country !== null;
});

const countries = ref([
    { id: 1, iso_alpha_2: 'us', name: 'United States', avatar: 'https://flagcdn.com/w20/us.png' },
    { id: 2, iso_alpha_2: 'si', name: 'Slovenia', avatar: 'https://flagcdn.com/w20/si.png' },
]);

// Determine default country based on locale
const defaultCountry = () => {
    if (locale.value.startsWith('en')) return countries.value.find(country => country.iso_alpha_2 === 'us');
    if (locale.value.startsWith('sl')) return countries.value.find(country => country.iso_alpha_2 === 'si');
    return countries.value[0];
};

const selectedCountry = ref(defaultCountry());
const userHasSelectedCountry = ref(false);

// Use window.location.search to get the query string from the URL
const queryParams = new URLSearchParams(window.location.search);

// Use a ref to reactively store the referral code, if present in the URL
const referralCode = ref(queryParams.get('referral') || '');
const email = queryParams.get('email') || '';

const form = useForm({
    name: '',
    email: email,
    password: '',
    password_confirmation: '',
    referral: referralCode.value,
    country: '',
    terms: false,
    cftoken: ''
});

// Watch for changes to selectedCountry and update the form accordingly
watch(selectedCountry, (newVal, oldVal) => {
    if (oldVal !== undefined && newVal !== oldVal) {
        userHasSelectedCountry.value = true;
    }
    form.country = newVal.iso_alpha_2;
}, { immediate: true });

watch(locale, (newLocale) => {
    if (!userHasSelectedCountry.value) {
        selectedCountry.value = defaultCountry();
    }
});

const submit = () => {
    isLoading.value = true;

    form.post(route('register'), {
        onFinish: () => {
            isLoading.value = false;
            form.reset('password', 'password_confirmation');
        }
    });
};

const handleSignUp = async () => {

    // Ensure terms are accepted
    if (!checkedNecessaryInputs.value) {
        alert('You must accept the terms to continue.');
        return;
    }

    try {
        isLoading.value = true;

        // Send the additional data to the backend to store in session
        await axios.post(route('register.google.init'), {
            country: form.country,
            terms: form.terms,
            referral: form.referral,
        });

        // Initiate OAuth by redirecting to the Google OAuth route
        const googleRegisterUrl = route('register.google');
        const urlWithParams = new URL(googleRegisterUrl, window.location.origin);

        // Append referral code if present
        if (referralCode.value) {
            urlWithParams.searchParams.append('referral', referralCode.value);
        }

        // Redirect the user to Google OAuth
        window.location.href = urlWithParams.toString();

    } catch (error) {
        console.error('Error initializing Google registration:', error);
        alert('An error occurred while starting the registration process. Please try again.');
    } finally {
        isLoading.value = false;
    }
};

</script>

<style scoped>
@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7); /* Tailwind Indigo-500 */
    }
    70% {
        box-shadow: 0 0 0 8px rgba(99, 102, 241, 0); /* Tailwind Indigo-500 */
    }
    100% {
        box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); /* Tailwind Indigo-500 */
    }
}

.pulse-checkbox {
    animation: pulse 2s infinite;
}
</style>

